<script setup lang="ts">
/**
 * App Layout Component
 *
 * Main application layout with sidebar, header, and global notification system.
 * SSE notification stream is opened here (once per authenticated session) to persist
 * across all page navigations without reconnecting.
 */
import { useNotificationSSE } from '@tms-phoenix/ui-vue';
import AppHeader from './AppHeader.vue';
import AppSidebar from './AppSidebar.vue';
import QuickActionFab from './QuickActionFab.vue';
import Breadcrumbs from '@/shared/components/navigation/BreadcrumbsComponent.vue';
import AppFooter from './AppFooter.vue';
import { CriticalAlertModal, RecallAlertBanner } from '@/shared/components/notifications';
import KeyInspectorOverlay from './KeyInspectorOverlay.vue';
import { useNotificationStore } from '@/stores';
import { useAuthStore, useEntitlementsStore, useUIStore } from '@/stores';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';

const route = useRoute();
const router = useRouter();

// Track in-flight navigations so we can show a loading indicator
const isNavigating = ref(false);
const stopNavStart = router.beforeEach(() => { isNavigating.value = true; });
const stopNavEnd = router.afterEach(() => { isNavigating.value = false; });
onUnmounted(() => { stopNavStart(); stopNavEnd(); });

const authStore = useAuthStore();
const uiStore = useUIStore();
const notificationStore = useNotificationStore();
const entitlementsStore = useEntitlementsStore();

// SSE housekeeping fetches fail silently to the user by design — a toast with
// no actionable context ("failed to fetch notifications") is worse than none.

// ── SSE Connection ──────────────────────────────────────────────────────────────
// Opens once per authenticated session. Persists across all route changes.
// Auto-reconnects on network failure with exponential backoff.
// Handles typed SSE events: NOTIFICATION, READ_RECEIPT, ACTIONED (multi-tab sync).
// Only enabled when RECALL entitlement is granted (notifications are recall-driven).
// SV-563: gated on hasActiveSession (not just isAuthenticated) so the streams connect
// reactively once authStore.validateSession() confirms the cookie session on page reload.
const sseEnabled = computed(() => authStore.hasActiveSession && entitlementsStore.hasEntitlement('RECALL'));

// useNotificationSSE never sends this value — auth flows through the httpOnly cookie
// (withCredentials). It is only a "session is ready" guard, and after a page reload there
// is no in-memory access token, so report the cookie session instead (SV-563).
const sseSessionGuard = () => (authStore.hasActiveSession ? 'cookie-session' : null);

// Snapshot of unread count taken when the SSE stream drops.
// Used on reconnect to decide whether a catch-up fetch is needed.
// Kept in setup-closure scope — not store state (UI-only concern).
let unreadCountAtDisconnect = 0;

useNotificationSSE({
  url: `${config.api.baseUrl.replace(/\/$/, '')}${API_ROUTES.NOTIFICATIONS}/notifications/stream`,
  getToken: sseSessionGuard,
  getUserId: () => authStore.staffId,
  enabled: sseEnabled,

  // New notification arrived via SSE
  onNotification: (notification) => {
    // TODO: Remove in production — SSE notification received log
    console.log('[SSE-DEBUG] RECEIVER: NOTIFICATION event received via SSE', {
      recipient_id: notification.recipient_id,
      event_type: notification.event_type,
      priority: notification.priority,
      subject: notification.subject,
      reference_entity_type: notification.reference_entity_type,
      reference_entity_id: notification.reference_entity_id,
      created_at: notification.created_at,
      timestamp: new Date().toISOString(),
    });
    notificationStore.handleIncoming(notification);
    // Priority routing:
    // CRITICAL → blocking modal (handled by store.criticalQueue)
    // HIGH / NORMAL / LOW → notification drawer only, no toast

    // When a new recall is dispatched to this hospital, refresh the pending
    // recalls list so the bell badge and pending section stay in sync.
    if (notification.reference_entity_type === 'PRODUCT_RECALL') {
        // TODO: Remove in production — recall pending refresh trigger log
        console.log('[SSE-DEBUG] RECEIVER: PRODUCT_RECALL notification — triggering fetchPendingRecalls');
      notificationStore.fetchPendingRecalls(getApiClient());
    }
  },

  // Multi-tab sync: another tab marked a notification as read
  onReadReceipt: (recipientId) => {
        // TODO: Remove in production — READ_RECEIPT received log
        console.log('[SSE-DEBUG] RECEIVER: READ_RECEIPT event received via SSE', {
          recipient_id: recipientId,
          timestamp: new Date().toISOString(),
        });
    notificationStore.handleReadReceipt(recipientId);
  },

  // Multi-tab sync: another tab acknowledged a CRITICAL alert
  onActioned: (recipientId) => {
        // TODO: Remove in production — ACTIONED received log
        console.log('[SSE-DEBUG] RECEIVER: ACTIONED event received via SSE', {
          recipient_id: recipientId,
          timestamp: new Date().toISOString(),
        });
    notificationStore.handleActioned(recipientId);
  },

  onConnectionChange: async (connected) => {
    notificationStore.setConnectionState(connected);

    if (!connected) {
      // Record the count at time of disconnect.
      // On reconnect we compare against this to detect missed events.
      unreadCountAtDisconnect = notificationStore.unreadCount;
            // TODO: Remove in production — SSE disconnect log
            console.warn('[SSE-DEBUG] CONNECTION: SSE stream DISCONNECTED', {
              unreadCountSnapshot: unreadCountAtDisconnect,
              timestamp: new Date().toISOString(),
            });
      return;
    }

    const api = getApiClient();

    if (!notificationStore.isInitialLoadComplete) {
            // TODO: Remove in production — first connect log
            console.log('[SSE-DEBUG] CONNECTION: SSE stream CONNECTED (first connect) — starting full hydration', {
              timestamp: new Date().toISOString(),
            });
      // ── First ever connect: full hydration ─────────────────────────────
      // Loads unread inbox, blocking critical alerts, and pending recalls.
      notificationStore.fetchInitialNotifications(api);
      notificationStore.fetchCriticalUnactioned(api);
      notificationStore.fetchPendingRecalls(api);
    } else {
            // TODO: Remove in production — reconnect log
            console.log('[SSE-DEBUG] CONNECTION: SSE stream RECONNECTED — running catch-up', {
              unreadCountAtDisconnect,
              currentUnreadCount: notificationStore.unreadCount,
              timestamp: new Date().toISOString(),
            });
      // ── Reconnect after a drop: lightweight catch-up ────────────────────
      // 1 cheap GET: server COUNT query only — avoids full inbox fetch when
      // nothing changed while disconnected (the common case).
      await notificationStore.fetchUnreadCount(api);

      // Only fetch the full missed list when new items actually arrived
      // while the stream was down (count changed since disconnect snapshot).
      if (notificationStore.unreadCount !== unreadCountAtDisconnect) {
                // TODO: Remove in production — missed notifications log
                console.log('[SSE-DEBUG] CONNECTION: unread count changed during disconnect — fetching missed notifications', {
                  before: unreadCountAtDisconnect,
                  after: notificationStore.unreadCount,
                  delta: notificationStore.unreadCount - unreadCountAtDisconnect,
                });
        await notificationStore.fetchMissedNotifications(api);
      }

      // Always re-sync pending recalls — recall status (Approve/Reject)
      // may have changed on another tab or by another user while offline.
      notificationStore.fetchPendingRecalls(api);
    }
  },
});

// ── Force-logout SSE connection ──────────────────────────────────────────────
// Independent from the RECALL-gated stream above (sseEnabled): force-logout must
// reach every authenticated user regardless of the RECALL entitlement, so this
// opens its own connection to the dedicated /stream/force-logout endpoint, keyed
// by Cognito username (not staffId/sub, which /stream validates as a UUID) —
// see notify-force-logout.util.ts on the backend.
useNotificationSSE({
  url: `${config.api.baseUrl.replace(/\/$/, '')}${API_ROUTES.NOTIFICATIONS}/notifications/stream/force-logout`,
  getToken: sseSessionGuard,
  getUserId: () => authStore.username,
  enabled: computed(() => authStore.hasActiveSession),
  // This connection never receives NOTIFICATION frames — only FORCE_LOGOUT.
  onNotification: () => { /* no-op — see useNotificationSSE above for the real notifications stream */ },
  onForceLogout: (payload) => {
    authStore.handleForcedLogout(payload.reason);
  },
});
</script>

<template>
  <div>
    <!-- Navigation progress bar — slides across the top while a route is resolving -->
    <div
      v-if="isNavigating"
      class="fixed top-0 inset-x-0 z-[100] h-[3px] overflow-hidden bg-primary-100"
    >
      <div class="absolute h-full w-1/3 animate-[nav-bar_1s_linear_infinite] bg-primary-500" />
    </div>

    <AppHeader />
    <!-- <AppSidebar /> -->

    <div class="lg:pl-0">
      <AppSidebar />
    </div>
    <div
      class="h-screen flex flex-col transition-[padding-left] duration-300"
      :class="uiStore.sidebarCollapsed ? 'lg:pl-[60px]' : 'lg:pl-56'"
    >
      <main class="mt-1 flex-1 overflow-hidden flex flex-col">
        <!-- Recall Alert Banner — full-width, sits between AppBar and breadcrumbs, inside h-screen budget -->
        <RecallAlertBanner />

        <div class="flex-1 min-h-0 flex flex-col px-4 sm:px-6 lg:px-4 pb-14">
          <!-- Breadcrumbs -->
          <div
            v-if="route.path !== '/'"
            class="shrink-0 px-4 pb-3"
            :class="notificationStore.isRecallBannerVisible ? 'pt-4' : 'pt-3'"
          >
            <Breadcrumbs />
          </div>

          <!-- Page content -->
          <div class="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
            <div
              v-if="isNavigating"
              class="flex h-full min-h-64 items-center justify-center"
            >
              <div class="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
            </div>
            <RouterView v-else />
          </div>
        </div>
      </main>
    </div>
    <AppFooter />

    <QuickActionFab />

    <!-- Critical Alert Modal — visible on ALL pages when CRITICAL notification arrives -->
    <CriticalAlertModal />

    <!-- Translation key inspector — banner + popover, both hidden unless an admin has turned inspect mode on -->
    <KeyInspectorOverlay />
  </div>
</template>
